import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/soapservices';

const EMPTY_FORM = {
  name: '', description: '', url: '', serviceNamespace: '',
  username: '', password: '', firmaKod: 1, calismaYili: 2026, distributorKod: 1,
  isActive: true, hasPassword: false,
};

const unwrap = (p) => (p && Object.prototype.hasOwnProperty.call(p, 'data') ? p.data : p);
const pick = (o, ...keys) => { for (const k of keys) { if (o && o[k] != null) return o[k]; } return undefined; };

function parsePlan(mappingJson) {
  if (!mappingJson) return { tables: [] };
  try {
    const p = typeof mappingJson === 'string' ? JSON.parse(mappingJson) : mappingJson;
    return { tables: Array.isArray(p?.tables) ? p.tables : [] };
  } catch { return { tables: [] }; }
}

// Otomatik öneri DIŞI tutulacak sistem kolonları (PK/tenant/audit) — kaynaktan yazılmamalı.
const SYSTEM_COLS = new Set(['id', 'tenantid', 'createdby', 'createddate', 'updatedby', 'updateddate', 'createduser', 'modifiedby', 'modifieddate']);

function suggestTarget(sourceName, targetColumns) {
  const norm = (x) => String(x).toLowerCase().replace(/[_\s]/g, '');
  const candidates = targetColumns.filter((c) => !SYSTEM_COLS.has(norm(c.name)));
  const sn = norm(sourceName);
  const exact = candidates.find((c) => norm(c.name) === sn);
  if (exact) return exact.name;
  const partial = candidates.find((c) => norm(c.name).includes(sn) || sn.includes(norm(c.name)));
  return partial ? partial.name : '';
}

// Türkçe karakter katlama + alfasayısal sadeleştirme (preset eşleştirmesi için).
const normKey = (x) => String(x).toLowerCase()
  .replace(/ı/g, 'i').replace(/i̇/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
  .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
  .replace(/[^a-z0-9]/g, '');

// Hazır şablonlar: hedef tablo seçilince kaynak alan adına göre otomatik eşleme.
// İlişki alanları için lookupEntity/lookupKeyProp ile kaynak kodu ilgili Guid'e çözülür.
const PRESETS = {
  Customer: {
    kod: { target: 'ErpId' },
    textkod: { target: 'TextId' },
    tekilkod: { target: 'UniqueCode' },
    ozelkod: { target: 'SpecialCode' },
    unvan: { target: 'Title' },
    vergitype: { target: 'TaxType' },
    vd: { target: 'TaxOffice' },
    vn: { target: 'TaxNumber' },
    tckimlikno: { target: 'IdentityNumber' },
    sehir: { target: 'City' },
    ilce: { target: 'Province' },
    postakod: { target: 'PostCode' },
    ulkeadi: { target: 'Country' },
    telefon: { target: 'Phone' },
    ceptelno: { target: 'CellPhone' },
    faks: { target: 'Fax' },
    cadde: { target: 'Street' },
    kapino: { target: 'ApartmentNo' },
    kisaad: { target: 'ShortName' },
    email: { target: 'Email' },
    www: { target: 'Web' },
    koordinatx: { target: 'Longtitude' },
    koordinaty: { target: 'Latitude' },
    cekvadegun: { target: 'CheckDueDate' },
    vadegun: { target: 'DueDate' },
    // İlişki (lookup) alanları. Vendor → yoksa HATA (rollback); Grup/AltGrup → yoksa otomatik OLUŞTUR.
    distkod: { target: 'VendorId', lookupEntity: 'Vendor', lookupKeyProp: 'ErpId' },
    disttextkod: { target: 'VendorId', lookupEntity: 'Vendor', lookupKeyProp: 'TextId' },
    ekgrupkod: { target: 'SubGroupId', lookupEntity: 'CustomerSubGroup', lookupKeyProp: 'TextId', createIfMissing: true },
    grupkod: { target: 'GroupId', lookupEntity: 'CustomerGroup', lookupKeyProp: 'TextId', createIfMissing: true },
  },
};

// Bir kaynak kolon için hedef eşlemesini üret (önce mevcut/elle, sonra preset, sonra isim önerisi).
function mapColumn(sourceName, targetTableName, targetCols, prevCol) {
  if (prevCol && prevCol.target) return { ...prevCol, source: sourceName };
  const preset = PRESETS[targetTableName]?.[normKey(sourceName)];
  if (preset) return { source: sourceName, target: preset.target, lookupEntity: preset.lookupEntity, lookupKeyProp: preset.lookupKeyProp, createIfMissing: preset.createIfMissing };
  return { source: sourceName, target: targetCols ? suggestTarget(sourceName, targetCols) : '' };
}

const WIZARD_STEPS = [
  { n: 1, title: 'Veri Seç & Önizle', hint: 'Hedef tablo + view + kriter → önizle, alanları eşle' },
  { n: 2, title: 'Aktar', hint: 'Aktarımı başlat ve izle' },
];

export function SoapServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  usePageMeta(isNew ? 'Yeni SOAP Servis' : 'SOAP Servis Detay', 'SOAP servis tanımı ve veri aktarımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [plan, setPlan] = useState({ tables: [] });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('connection');
  const [toast, setToast] = useState(null);
  const [changePassword, setChangePassword] = useState(isNew);

  // Menü aksiyon hakları: yeni kayıt Add, mevcut kayıt Update ister.
  // Sihirbaz yalnız kayıtlı serviste açılır → eşleştirme/aktarım aksiyonları canEdit ister.
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  const [targetTables, setTargetTables] = useState([]);
  const [step, setStep] = useState(1);
  const [discoveringTable, setDiscoveringTable] = useState(null);
  const [samples, setSamples] = useState({}); // sourceTable -> [örnek satır...] (geçici, kaydedilmez)

  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 4000); };
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`SOAP servisi alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm({
        name: pick(r, 'name', 'Name') ?? '',
        description: pick(r, 'description', 'Description') ?? '',
        url: pick(r, 'url', 'Url') ?? '',
        serviceNamespace: pick(r, 'serviceNamespace', 'ServiceNamespace') ?? '',
        username: pick(r, 'username', 'Username') ?? '',
        password: '',
        firmaKod: pick(r, 'firmaKod', 'FirmaKod') ?? 1,
        calismaYili: pick(r, 'calismaYili', 'CalismaYili') ?? 2026,
        distributorKod: pick(r, 'distributorKod', 'DistributorKod') ?? 1,
        isActive: pick(r, 'isActive', 'IsActive') ?? true,
        hasPassword: pick(r, 'hasPassword', 'HasPassword') ?? false,
      });
      setPlan(parsePlan(pick(r, 'mappingJson', 'MappingJson')));
      setChangePassword(false);
    } catch (err) {
      showToast('error', err.message || 'SOAP servisi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_BASE}/target-tables`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((p) => setTargetTables(unwrap(p) || []))
      .catch(() => {});
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const buildBody = (mappingOverride) => ({
    id: isNew ? null : id,
    name: form.name.trim(),
    description: form.description?.trim() || null,
    url: form.url.trim(),
    serviceNamespace: form.serviceNamespace?.trim() || null,
    username: form.username?.trim() || null,
    password: changePassword ? (form.password || null) : null,
    firmaKod: Number(form.firmaKod) || 0,
    calismaYili: Number(form.calismaYili) || 0,
    distributorKod: Number(form.distributorKod) || 0,
    mappingJson: JSON.stringify(mappingOverride || plan),
    isActive: form.isActive,
  });

  const validateConnection = () => {
    if (!form.name.trim()) return 'Ad zorunludur.';
    if (!form.url.trim()) return 'Url zorunludur.';
    return null;
  };

  const handleSave = async (mappingOverride) => {
    const err = validateConnection();
    if (err) { showToast('error', err); return null; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(buildBody(mappingOverride)) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'message', 'Message') || `İşlem başarısız (${res.status})`);
      showToast('success', isNew ? 'SOAP servisi oluşturuldu.' : 'Değişiklikler kaydedildi.');
      if (isNew) {
        const newId = pick(unwrap(payload), 'id', 'Id');
        if (newId) { navigate(`/soap-services/${newId}`, { replace: true }); return newId; }
      }
      return id;
    } catch (e) {
      showToast('error', e.message || 'İşlem başarısız');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const connBody = () => ({
    id: isNew ? null : id,
    url: form.url.trim(),
    serviceNamespace: form.serviceNamespace?.trim() || null,
    username: form.username?.trim() || null,
    password: changePassword ? (form.password || null) : null,
    firmaKod: Number(form.firmaKod) || 0,
    calismaYili: Number(form.calismaYili) || 0,
    distributorKod: Number(form.distributorKod) || 0,
  });

  // Bir görünümü canlı çağırıp alanları + örnek satırları getir, hedefe otomatik ön-eşle.
  const discoverColumns = async (view) => {
    if (!form.url.trim()) { showToast('error', 'Önce Bağlantı sekmesinde Url girin.'); return; }
    if (!view.sourceTable?.trim()) { showToast('error', 'View / Tablo adı girin.'); return; }
    setDiscoveringTable(view.sourceTable);
    try {
      const body = { ...connBody(), tabloAdi: view.sourceTable.trim(), viewAdi: view.viewName?.trim() || null, kriter: view.kriter?.trim() || null };
      const res = await fetch(`${API_BASE}/discover`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'message', 'Message') || `Önizleme başarısız (${res.status})`);
      const result = unwrap(payload);
      const cols = result?.columns || [];
      const tgt = findTargetTable(view.targetTable);
      // Mevcut elle eşlemeleri koru; yeni kolonlara preset/öneri uygula.
      const existing = new Map((view.columns || []).map((c) => [c.source, c]));
      updateTable(view.sourceTable, {
        columns: cols.map((c) => mapColumn(c.name, view.targetTable, tgt?.columns, existing.get(c.name))),
      });
      setSamples((prev) => ({ ...prev, [view.sourceTable]: result?.sampleRows || [] }));
      showToast(cols.length ? 'success' : 'info', cols.length ? `${cols.length} alan getirildi (${result.sampleRowCount} satır).` : 'Veri gelmedi. View adı / kriteri kontrol edin.');
    } catch (e) {
      showToast('error', e.message || 'Önizleme başarısız');
    } finally {
      setDiscoveringTable(null);
    }
  };

  // --- plan yardımcıları ---
  const findTargetTable = (name) => targetTables.find((t) => t.table === name);

  const addView = () => setPlan((prev) => ({
    tables: [...prev.tables, { sourceTable: '', viewName: '', kriter: '', targetTable: '', keyColumn: '', mode: 'insert', columns: [] }],
  }));

  const removeView = (index) => setPlan((prev) => ({ tables: prev.tables.filter((_, i) => i !== index) }));

  const updateViewByIndex = (index, patch) => setPlan((prev) => ({
    tables: prev.tables.map((t, i) => (i === index ? { ...t, ...patch } : t)),
  }));

  const updateTable = (sourceTable, patch) => setPlan((prev) => ({
    tables: prev.tables.map((t) => (t.sourceTable === sourceTable ? { ...t, ...patch } : t)),
  }));

  const onTargetTableChange = (sourceTable, targetTableName) => {
    const tgt = findTargetTable(targetTableName);
    setPlan((prev) => ({
      tables: prev.tables.map((t) => {
        if (t.sourceTable !== sourceTable) return t;
        // Hedef tablo değişti → eşlemeleri yeni tabloya göre (preset/öneri) yeniden üret.
        const columns = (t.columns || []).map((c) => mapColumn(c.source, targetTableName, tgt?.columns, null));
        return { ...t, targetTable: targetTableName, keyColumn: '', columns };
      }),
    }));
  };

  // Elle hedef seçimi: lookup bilgisini temizle (artık düz kolon eşlemesi).
  const updateColumnMapping = (sourceTable, sourceCol, targetCol) => setPlan((prev) => ({
    tables: prev.tables.map((t) => {
      if (t.sourceTable !== sourceTable) return t;
      return { ...t, columns: t.columns.map((c) => (c.source === sourceCol ? { ...c, target: targetCol, lookupEntity: undefined, lookupKeyProp: undefined } : c)) };
    }),
  }));

  const selectedTables = plan.tables;
  // Tek adımda her şey: her görünümde kaynak + hedef + en az bir eşlenmiş alan olmalı.
  const canRun = selectedTables.length > 0 &&
    selectedTables.every((t) => t.sourceTable?.trim() && t.targetTable && (t.columns || []).some((c) => c.target));

  // --- aktarım + progress ---
  const startPolling = useCallback((serviceId, theRunId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE}/${serviceId}/import-logs?runId=${theRunId}`, { headers: authHeaders() });
        const payload = await res.json().catch(() => null);
        const data = unwrap(payload) || [];
        setLogs(data);
        const totalT = selectedTables.length;
        const done = data.filter((l) => l.status === 2 || l.status === 3).length;
        if (totalT > 0 && done >= totalT) {
          clearInterval(pollRef.current); pollRef.current = null;
          setRunning(false);
          const failed = data.filter((l) => l.status === 3).length;
          showToast(failed ? 'error' : 'success', failed ? `Aktarım bitti, ${failed} tabloda hata.` : 'Aktarım başarıyla tamamlandı.');
        }
      } catch { /* poll continues */ }
    };
    pollRef.current = setInterval(tick, 1500);
    tick();
  }, [selectedTables.length]);

  const handleRun = async () => {
    if (!canRun) { showToast('error', 'Her görünüm için hedef ve en az bir alan eşleştirilmeli.'); return; }
    const savedId = await handleSave(plan);
    if (!savedId) return;
    setRunning(true); setLogs([]);
    try {
      const res = await fetch(`${API_BASE}/${savedId}/run`, { method: 'POST', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'message', 'Message') || 'Aktarım başlatılamadı');
      const newRunId = pick(unwrap(payload), 'id', 'Id') || unwrap(payload);
      startPolling(savedId, newRunId);
    } catch (e) {
      setRunning(false);
      showToast('error', e.message || 'Aktarım başlatılamadı');
    }
  };

  const totalViews = selectedTables.length;
  const doneCount = logs.filter((l) => l.status === 2 || l.status === 3).length;
  const percent = totalViews > 0 ? Math.round((doneCount / totalViews) * 100) : 0;

  const TABS = [
    { key: 'connection', label: 'Bağlantı', icon: '🔌' },
    { key: 'wizard', label: 'Aktarım Sihirbazı', icon: '🧙' },
  ];

  const logStatusMeta = (st) => ({
    1: { label: 'Çalışıyor', tone: 'wait' },
    2: { label: 'Başarılı', tone: 'ok' },
    3: { label: 'Hatalı', tone: 'bad' },
  }[st] || { label: 'Bekliyor', tone: 'neutral' });

  return (
    <div style={{ ...s.container, padding: '4px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <button style={s.btnSecondary} onClick={() => navigate('/soap-services')}>← Geri</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }} aria-hidden="true">🔗</span>
          <span style={{ fontSize: 19, fontWeight: 800 }}>{form.name || (isNew ? 'Yeni SOAP Servis' : '...')}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          {canSave && (
            <button style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={() => handleSave()} disabled={saving}>
              {saving ? 'Kaydediliyor...' : (isNew ? 'Oluştur' : 'Kaydet')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary, #6b7280)' }}>Yükleniyor…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.1))', marginBottom: 20 }}>
            {TABS.map((t) => {
              const disabled = isNew && t.key === 'wizard';
              const active = activeTab === t.key;
              return (
                <button key={t.key} disabled={disabled} onClick={() => setActiveTab(t.key)}
                  title={disabled ? 'Önce servisi kaydedin' : undefined}
                  style={{
                    border: 'none', background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
                    padding: '11px 18px', fontSize: 14, fontWeight: active ? 800 : 500,
                    color: disabled ? 'var(--text-disabled, #c0c4cc)' : active ? 'var(--accent)' : 'var(--text-primary)',
                    borderBottom: active ? '2.5px solid var(--accent)' : '2.5px solid transparent',
                  }}>
                  <span aria-hidden="true" style={{ marginRight: 7 }}>{t.icon}</span>{t.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'connection' && (
            <ConnectionTab form={form} set={set} isNew={isNew} changePassword={changePassword} setChangePassword={setChangePassword} />
          )}

          {activeTab === 'wizard' && !isNew && (
            <WizardTab
              step={step} setStep={setStep}
              targetTables={targetTables} plan={plan} samples={samples}
              addView={addView} removeView={removeView} updateViewByIndex={updateViewByIndex}
              onTargetTableChange={onTargetTableChange} updateTable={updateTable} updateColumnMapping={updateColumnMapping}
              findTargetTable={findTargetTable}
              discoverColumns={discoverColumns} discoveringTable={discoveringTable}
              canRun={canRun} canEdit={canEdit}
              onRun={handleRun} running={running} saving={saving}
              logs={logs} percent={percent} logStatusMeta={logStatusMeta}
            />
          )}
        </>
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ===================== Bağlantı Tab =====================
function ConnectionTab({ form, set, isNew, changePassword, setChangePassword }) {
  // GEÇİCİ: test bağlantı bilgilerini doldurur. Test bitince bu buton + fonksiyon kaldırılacak.
  const fillTestData = () => {
    setChangePassword(true);
    set({
      name: form.name || 'Test Servis',
      url: 'https://panoramamey.com/integrationwebservice.asmx',
      serviceNamespace: atob('aHR0cDovL2ludGVncmF0aW9uLnVuaXZlcmEuY29tLnRy'),
      username: 'vispera',
      password: '12345678',
      firmaKod: 1, calismaYili: 2026, distributorKod: 1,
      isActive: true,
    });
  };

  return (
    <div style={cardStyle}>
      {/* GEÇİCİ test butonu — kaldırılacak */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={fillTestData}
          style={{ border: '1.5px dashed var(--warn)', background: 'rgba(253,171,61,0.08)', color: '#b45309',
            borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          ⚡ Test bilgilerini doldur (geçici)
        </button>
      </div>

      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>Ad *</label>
          <input style={s.input} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Örn. Müşteri Entegrasyon Servisi" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Açıklama</label>
          <input style={s.input} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Opsiyonel" />
        </div>
      </div>

      <div style={s.formRow}>
        <div style={{ ...s.formGroup, flex: 2 }}>
          <label style={s.label}>Servis Url *</label>
          <input style={s.input} value={form.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://ornek.com/servis.asmx" />
        </div>
        <div style={{ ...s.formGroup, flex: 2 }}>
          <label style={s.label}>Servis Namespace *</label>
          <input style={s.input} value={form.serviceNamespace} onChange={(e) => set({ serviceNamespace: e.target.value })} placeholder="http://ornek.com/" />
        </div>
      </div>

      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>Kullanıcı Adı</label>
          <input style={s.input} value={form.username} onChange={(e) => set({ username: e.target.value })} autoComplete="off" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Şifre</label>
          {!isNew && form.hasPassword && !changePassword ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input style={{ ...s.input, flex: 1 }} value="••••••••" disabled readOnly />
              <button type="button" style={s.btnSecondary} onClick={() => { setChangePassword(true); set({ password: '' }); }}>Değiştir</button>
            </div>
          ) : (
            <input style={s.input} type="password" value={form.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" placeholder={isNew ? '' : 'Yeni şifre'} />
          )}
        </div>
      </div>

      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>Firma Kodu</label>
          <input style={s.input} type="number" value={form.firmaKod} onChange={(e) => set({ firmaKod: e.target.value })} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Çalışma Yılı</label>
          <input style={s.input} type="number" value={form.calismaYili} onChange={(e) => set({ calismaYili: e.target.value })} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Distribütör Kodu</label>
          <input style={s.input} type="number" value={form.distributorKod} onChange={(e) => set({ distributorKod: e.target.value })} />
        </div>
      </div>

      <div style={s.formRow}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
          <span style={s.label}>Aktif</span>
        </label>
      </div>

      <div style={{ marginTop: 8, paddingTop: 12, borderTop: '1px dashed var(--border-color, rgba(15,23,42,0.12))', fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
        Bağlantıyı kaydettikten sonra <strong>Aktarım Sihirbazı</strong> sekmesinde aktarılacak görünümleri (tablo/view/kriter) tanımlayın.
      </div>
    </div>
  );
}

// ===================== Sihirbaz Tab (2 adım) =====================
function WizardTab(props) {
  const {
    step, setStep, targetTables, plan, samples, addView, removeView, updateViewByIndex,
    onTargetTableChange, updateColumnMapping, findTargetTable,
    discoverColumns, discoveringTable, canRun, canEdit,
    onRun, running, saving, logs, percent, logStatusMeta, updateTable,
  } = props;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
        {WIZARD_STEPS.map((stp) => {
          const active = step === stp.n;
          const done = step > stp.n;
          return (
            <div key={stp.n} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 10,
              background: active ? 'rgba(0,115,234,0.10)' : done ? 'rgba(0,200,117,0.10)' : 'var(--surface-2, #f3f4f6)',
              border: active ? '1.5px solid var(--accent)' : '1.5px solid transparent', flex: '1 1 220px',
            }}>
              <span style={{
                width: 26, height: 26, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 13, color: '#fff', background: active ? 'var(--accent)' : done ? 'var(--success)' : '#9ca3af',
              }}>{done ? '✓' : stp.n}</span>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{stp.title}</span>
                <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{stp.hint}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={cardStyle}>
        {/* Adım 1: veri seç & önizle & eşle */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {plan.tables.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary, #6b7280)', padding: 14 }}>
                Aktarılacak veri yok. <strong>+ Veri Kaynağı Ekle</strong> ile başlayın.
              </div>
            )}
            {plan.tables.map((t, i) => {
              const tgt = findTargetTable(t.targetTable);
              const sampleRows = samples[t.sourceTable] || [];
              const firstRow = sampleRows[0] || {};
              const busy = discoveringTable === t.sourceTable;
              const mappedCount = (t.columns || []).filter((c) => c.target).length;
              return (
                <div key={i} style={subCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <strong>Veri Kaynağı #{i + 1}</strong>
                    {canEdit && <button type="button" style={s.btnDanger} onClick={() => removeView(i)}>Sil</button>}
                  </div>

                  {/* Hedef + mod + anahtar */}
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Hedef Tablo *</label>
                      <select style={s.select} value={t.targetTable} onChange={(e) => onTargetTableChange(t.sourceTable, e.target.value)}>
                        <option value="">— Seçin —</option>
                        {targetTables.map((tt) => <option key={tt.table} value={tt.table}>{tt.table} ({tt.entity})</option>)}
                      </select>
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Mod</label>
                      <select style={s.select} value={t.mode} onChange={(e) => updateTable(t.sourceTable, { mode: e.target.value })}>
                        <option value="insert">Sadece Ekle</option>
                        <option value="upsert">Ekle / Güncelle</option>
                      </select>
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Anahtar Kolon {t.mode === 'upsert' ? '*' : ''}</label>
                      <select style={s.select} value={t.keyColumn || ''} disabled={t.mode !== 'upsert' || !tgt}
                        onChange={(e) => updateTable(t.sourceTable, { keyColumn: e.target.value })}>
                        <option value="">— Seçin —</option>
                        {(tgt?.columns || []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Kaynak view + kriter + önizle */}
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>View Adı *</label>
                      <input style={s.input} value={t.sourceTable} onChange={(e) => updateViewByIndex(i, { sourceTable: e.target.value })} placeholder="VIEWMUSTERIGENERIC" />
                    </div>
                    <div style={{ ...s.formGroup, flex: 2 }}>
                      <label style={s.label}>Kriter</label>
                      <input style={s.input} value={t.kriter || ''} onChange={(e) => updateViewByIndex(i, { kriter: e.target.value })} placeholder={`"KOLON IN ('1002')"`} />
                    </div>
                    {/* Alan eşleştirmesini üreten aksiyon → canEdit ister. */}
                    {canEdit && (
                      <div style={{ ...s.formGroup, flex: '0 0 auto', justifyContent: 'flex-end' }}>
                        <label style={s.label}>&nbsp;</label>
                        <button type="button" style={{ ...s.btnPrimary, opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap' }}
                          disabled={busy} onClick={() => discoverColumns(t)}>
                          {busy ? 'Önizleniyor…' : '👁 Önizle & Eşle'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Eşleştirme tablosu (örnek değerlerle) */}
                  {(t.columns || []).length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginBottom: 6 }}>
                        {t.columns.length} alan · <span style={{ color: '#16a34a', fontWeight: 700 }}>{mappedCount} eşlendi</span>
                        {t.targetTable ? '' : ' · önce hedef tablo seçin'}
                      </div>
                      <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--border-color, rgba(15,23,42,0.08))', borderRadius: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', color: 'var(--text-secondary, #6b7280)', position: 'sticky', top: 0, background: 'var(--surface-1, #fff)' }}>
                              <th style={thStyle}>Kaynak Alan</th>
                              <th style={thStyle}>Örnek Değer</th>
                              <th style={thStyle}>Hedef Kolon</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.columns.map((c) => {
                              const ex = firstRow[c.source];
                              const exText = ex == null || ex === '' ? '—' : String(ex);
                              return (
                                <tr key={c.source} style={{ borderTop: '1px solid var(--border-color, rgba(15,23,42,0.08))' }}>
                                  <td style={tdStyle}><span className="c360-erp-chip">{c.source}</span></td>
                                  <td style={{ ...tdStyle, color: 'var(--text-secondary, #6b7280)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={exText}>{exText.length > 36 ? exText.slice(0, 36) + '…' : exText}</td>
                                  <td style={tdStyle}>
                                    <select style={{ ...s.select, minWidth: 220, borderColor: c.target ? 'var(--success)' : undefined }}
                                      value={c.target} onChange={(e) => updateColumnMapping(t.sourceTable, c.source, e.target.value)}>
                                      <option value="">— Aktarma —</option>
                                      {(tgt?.columns || []).map((tc) => <option key={tc.name} value={tc.name}>{tc.name}{tc.isKey ? ' 🔑' : ''}</option>)}
                                    </select>
                                    {c.lookupEntity && (
                                      <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2 }}>
                                        ↳ ilişki: {c.lookupEntity}.{c.lookupKeyProp} → Id
                                        <span style={{ color: c.createIfMissing ? '#16a34a' : '#dc2626' }}>
                                          {c.createIfMissing ? ' · yoksa oluşturulur' : ' · yoksa hata (rollback)'}
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {canEdit && <button type="button" style={s.btnSecondary} onClick={addView}>+ Veri Kaynağı Ekle</button>}
          </div>
        )}

        {/* Adım 2: özet & aktar & ilerleme */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <p style={{ ...s.label, marginBottom: 10 }}>Aktarım Özeti</p>
              {plan.tables.map((t) => {
                const mapped = (t.columns || []).filter((c) => c.target).length;
                const ok = t.sourceTable && t.targetTable && mapped > 0;
                return (
                  <div key={t.sourceTable} style={{ ...subCardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <strong>{t.sourceTable || <em style={{ color: '#e53e3e' }}>view yok</em>}</strong> → {t.targetTable || <em style={{ color: '#e53e3e' }}>hedef yok</em>}
                      <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>{mapped} alan · {t.mode === 'upsert' ? 'ekle/güncelle' : 'ekle'}</span>
                    </div>
                    <span style={{ color: ok ? '#16a34a' : '#e53e3e', fontWeight: 700 }}>{ok ? '✓ hazır' : '⚠ eksik'}</span>
                  </div>
                );
              })}
              {!canRun && <div style={{ color: '#e53e3e', fontSize: 13, marginTop: 6 }}>Her kaynak için view, hedef tablo ve en az bir eşlenmiş alan gerekli. Adım 1'e dönüp tamamlayın.</div>}
            </div>

            {(running || logs.length > 0) && (
              <div style={subCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong>Aktarım İlerlemesi</strong>
                  <span>{percent}%</span>
                </div>
                <div style={{ height: 12, background: 'var(--surface-2, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${percent}%`, height: '100%', background: 'linear-gradient(90deg,var(--accent),var(--success))', transition: 'width .4s ease' }} />
                </div>
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {logs.map((l) => {
                    const m = logStatusMeta(l.status);
                    // status=2 (başarılı) + errorMessage = otomatik tip dönüşümü uyarısı (amber).
                    const isWarn = l.status === 2 && l.errorMessage;
                    return (
                      <div key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <StatusBadge tone={m.tone}>{m.label}</StatusBadge>
                          <span style={{ fontWeight: 600 }}>{l.sourceTable} → {l.targetTable}</span>
                          {l.rowsWritten != null && <span style={{ color: 'var(--text-secondary, #6b7280)' }}>{l.rowsWritten}/{l.rowsRead ?? '?'} satır</span>}
                        </div>
                        {l.errorMessage && (
                          <div style={{ color: isWarn ? '#b45309' : '#e53e3e', fontSize: 12, paddingLeft: 4, whiteSpace: 'pre-line', maxHeight: 220, overflow: 'auto' }}>
                            {isWarn ? '⚠ ' : '✗ '}{isWarn ? String(l.errorMessage).slice(0, 400) + (String(l.errorMessage).length > 400 ? '…' : '') : String(l.errorMessage)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
        <button type="button" style={{ ...s.btnSecondary, visibility: step > 1 ? 'visible' : 'hidden' }} onClick={() => setStep(step - 1)}>← Geri</button>
        {step < 2 ? (
          <button type="button"
            style={{ ...s.btnPrimary, opacity: !canRun ? 0.5 : 1 }}
            disabled={!canRun}
            title={!canRun ? 'Her kaynak için hedef tablo ve en az bir eşlenmiş alan gerekli.' : undefined}
            onClick={() => setStep(2)}>
            İleri →
          </button>
        ) : (
          /* Aktar = kaydet + migration çalıştır → canEdit ister. */
          canEdit && (
            <button type="button"
              style={{ ...s.btnPrimary, opacity: (!canRun || running || saving) ? 0.5 : 1 }}
              disabled={!canRun || running || saving}
              onClick={onRun}>
              {running ? 'Aktarılıyor…' : '🚀 Aktar'}
            </button>
          )
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: 'var(--surface-1, #fff)', border: '1px solid var(--border-color, rgba(15,23,42,0.10))',
  borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
};
const subCardStyle = {
  background: 'var(--surface-2, #f9fafb)', border: '1px solid var(--border-color, rgba(15,23,42,0.08))',
  borderRadius: 12, padding: 14,
};
const thStyle = { padding: '6px 8px', fontWeight: 600 };
const tdStyle = { padding: '8px' };

export default SoapServiceDetailPage;
